/**
 * Which rows of a long list are near its scroller's viewport.
 *
 * Wraps the framework-free virtualizer as an external store. The instance
 * mutates in place, so React reads it through `useSyncExternalStore` and gets
 * a new snapshot whenever the window moves, never the instance itself.
 *
 * @module components/ui/useWindowedRows
 */

import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from "react";

import {
  elementScroll,
  observeElementOffset,
  observeElementRect,
  type VirtualItem,
  Virtualizer,
} from "@tanstack/virtual-core";

export interface WindowedRowsOptions {
  count: number;
  scrollRef: RefObject<HTMLElement | null>;
  /** A first guess at a row's height, replaced once the row is measured. */
  estimateSize: number;
  /** Keeps a measured height with its row when rows are added or reordered. */
  getItemKey: (index: number) => string | number;
  /** Rows drawn past each edge, so a fast scroll does not show blank space. */
  overscan: number;
}

export interface WindowedRows {
  items: readonly VirtualItem[];
  /** Height of every row, drawn or not. */
  totalSize: number;
  /** For each drawn row's element, with `data-index` set on it. */
  measure: (node: HTMLElement | null) => void;
}

interface Snapshot {
  items: readonly VirtualItem[];
  totalSize: number;
}

/**
 * A guess at the scroller's height until it is measured. Without one nothing
 * is drawn on the first commit, and a table reads its column widths there.
 */
const INITIAL_RECT = { width: 0, height: 600 };

/** The virtualizer and the listeners waiting on it, as one store. */
class WindowStore {
  readonly virtualizer: Virtualizer<HTMLElement, HTMLElement>;

  private listeners = new Set<() => void>();

  private snapshot: Snapshot;

  /**
   * Starts with the rows at the top already in the snapshot, so the first
   * commit draws some: a table reads its natural column widths from them.
   */
  constructor({ count, scrollRef, estimateSize, getItemKey, overscan }: WindowedRowsOptions) {
    this.virtualizer = new Virtualizer<HTMLElement, HTMLElement>({
      count,
      getScrollElement: () => scrollRef.current,
      estimateSize: () => estimateSize,
      getItemKey,
      overscan,
      scrollToFn: elementScroll,
      observeElementRect,
      observeElementOffset,
      initialRect: INITIAL_RECT,
    });
    this.snapshot = {
      items: this.virtualizer.getVirtualItems(),
      totalSize: this.virtualizer.getTotalSize(),
    };
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): Snapshot => this.snapshot;

  /** Takes a fresh snapshot and tells React, once the window moves or the options change. */
  publish = (): void => {
    this.snapshot = {
      items: this.virtualizer.getVirtualItems(),
      totalSize: this.virtualizer.getTotalSize(),
    };
    this.listeners.forEach((listener) => {
      listener();
    });
  };
}

export function useWindowedRows({
  count,
  scrollRef,
  estimateSize,
  getItemKey,
  overscan,
}: WindowedRowsOptions): WindowedRows {
  const [store] = useState(
    () => new WindowStore({ count, scrollRef, estimateSize, getItemKey, overscan }),
  );
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  useLayoutEffect(() => store.virtualizer._didMount(), [store]);

  // Passed on every change and before the instance reads them, as the
  // library's own adapters do.
  useLayoutEffect(() => {
    store.virtualizer.setOptions({
      count,
      getScrollElement: () => scrollRef.current,
      estimateSize: () => estimateSize,
      getItemKey,
      overscan,
      scrollToFn: elementScroll,
      observeElementRect,
      observeElementOffset,
      initialRect: INITIAL_RECT,
      onChange: store.publish,
    });
    store.publish();
  }, [store, count, scrollRef, estimateSize, getItemKey, overscan]);

  // Attaches to the scroller, or follows it if it changed. Passive, and after
  // every commit: the scroller is an ancestor, and an ancestor's ref is not
  // yet set when this component's layout effects run.
  useEffect(() => {
    store.virtualizer._willUpdate();
  });

  const measure = useCallback(
    (node: HTMLElement | null) => {
      store.virtualizer.measureElement(node);
    },
    [store],
  );

  return { ...snapshot, measure };
}
